import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import {
  doc,
  setDoc,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { auth, db, googleProvider } from '../config/firebase';
import { AuthContextType, UserProfile, UserStatus } from '../types/auth';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let unsubscribeFirestore: Unsubscribe | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      setError(null);

      if (unsubscribeFirestore) {
        unsubscribeFirestore();
        unsubscribeFirestore = null;
      }

      if (firebaseUser) {
        setCurrentUser(firebaseUser);
        const userDocRef = doc(db, 'users', firebaseUser.uid);

        // Real-time listener for user_status changes
        unsubscribeFirestore = onSnapshot(
          userDocRef,
          async (snapshot) => {
            if (snapshot.exists()) {
              const data = snapshot.data() as UserProfile;
              setUserProfile(data);
              setLoading(false);
            } else {
              // First-time login: Initialize profile with user_status = 'NEW'
              const initialProfile: UserProfile = {
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                displayName: firebaseUser.displayName || 'Finance User',
                photoURL: firebaseUser.photoURL,
                user_status: 'NEW',
                role: 'USER',
                createdAt: new Date().toISOString(),
                lastLoginAt: new Date().toISOString(),
              };

              try {
                await setDoc(userDocRef, initialProfile);
                setUserProfile(initialProfile);
              } catch (err: any) {
                console.error('Failed to initialize user document:', err);
                setError(err.message || 'Error initializing profile in Firestore');
              } finally {
                setLoading(false);
              }
            }
          },
          (err) => {
            console.error('Firestore snapshot error:', err);
            setError(err.message);
            setLoading(false);
          }
        );
      } else {
        setCurrentUser(null);
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeFirestore) {
        unsubscribeFirestore();
      }
    };
  }, []);

  const signInWithGoogle = async () => {
    setError(null);
    setLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err: any) {
      console.error('Google sign-in error:', err);
      setError(err.message || 'Failed to sign in with Google');
      setLoading(false);
    }
  };

  const signOutUser = async () => {
    try {
      await signOut(auth);
      setUserProfile(null);
      setCurrentUser(null);
    } catch (err: any) {
      console.error('Sign-out error:', err);
      setError(err.message || 'Failed to sign out');
    }
  };

  const devToggleApproval = async () => {
    if (!currentUser) return;
    setError(null);
    const nextStatus: UserStatus = userProfile?.user_status === 'APPROVED' ? 'NEW' : 'APPROVED';
    const userDocRef = doc(db, 'users', currentUser.uid);

    try {
      const payload: Partial<UserProfile> = {
        uid: currentUser.uid,
        email: currentUser.email,
        displayName: currentUser.displayName || 'Finance User',
        photoURL: currentUser.photoURL,
        user_status: nextStatus,
        role: userProfile?.role || 'USER',
        lastLoginAt: new Date().toISOString(),
      };
      if (!userProfile) {
        payload.createdAt = new Date().toISOString();
      }
      await setDoc(userDocRef, payload, { merge: true });
    } catch (err: any) {
      console.error('Failed to dev-toggle approval status:', err);
      setError(`Dev Toggle failed: ${err.message}`);
      throw err;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        userProfile,
        loading,
        error,
        signInWithGoogle,
        signOutUser,
        devToggleApproval,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
